import { DotBadge } from '../CellBadge';
import { createColumn } from './createColumn';
import { ButtonGroupField } from 'components';
import { TransparencyIcon } from 'components/Icon';
import { fmt, parseBig, sepFormat } from 'utilities';
import { Box, Stack } from '@ff-it/ui';
import { ReactElement, ReactNode } from 'react';
import {
  DialogEditor,
  RenderCellProps,
  RenderEditCellProps,
  RenderSummaryCellProps,
  textEditor,
} from 'components/DataGrid';
import { GridGroup, GridRow, GridSummaryRow, RowOrGroup } from '../types';
import { transparencyOptions } from 'options';
import { CheckField, DecimalField } from '@ff-it/form';
import { usePlanController } from '../../usePlanController';
import { budgetEditor, clientColumn } from './style.css';
import { Budget } from '../../Budget';

type ViewRowProps = {
  label: ReactNode;
  value: ReactNode;
};
function ViewRow({ label, value }: ViewRowProps): ReactElement {
  return (
    <Stack direction="horizontal" align="center" lineHeight="xs">
      <Box display="flex" alignItems="center" width="2/5" fontSize="xs">
        {label}
      </Box>
      <Box width="full" textAlign="end">
        {value}
      </Box>
    </Stack>
  );
}

function View({ row }: RenderCellProps<GridRow, any>): ReactNode {
  const {
    planned_budget,
    sums: {
      planned: { client_net_total: planned_income },
      target: { client_net_total: target_income },
    },
  } = row;

  if (row.kind === 'SMART') {
    if (row.direction === 'CLIENT' || row.direction === 'BOTH') {
      return (
        <>
          <ViewRow label="Planned" value={<div data-testid="planned">{sepFormat(planned_income)}</div>} />
          <ViewRow
            label={<div className="font-weight-bold">Actual</div>}
            value={
              <div className="font-weight-bold" data-testid="target">
                {sepFormat(target_income)}
              </div>
            }
          />
        </>
      );
    }
    return null;
  }

  const transparency = row.client_total.transparency;
  const renderTarget = target_income !== planned_income;

  // danger if overspent
  const overspentColor = planned_budget?.is_overspent ? 'danger' : undefined;

  return (
    <>
      {planned_budget && <DotBadge />}
      <ViewRow
        label={titles[transparency]}
        value={
          <>
            <Box
              data-testid="planned"
              fontWeight={!renderTarget ? 'bold' : undefined}
              color={!renderTarget ? overspentColor : undefined}
              data-overspent={planned_budget?.is_overspent ? 'true' : undefined}
            >
              {sepFormat(planned_income)}
            </Box>
            {renderTarget && (
              <Box
                data-testid="target"
                fontWeight="bold"
                color={overspentColor}
                data-overspent={planned_budget?.is_overspent ? 'true' : undefined}
              >
                {sepFormat(target_income)}
              </Box>
            )}
          </>
        }
      />
    </>
  );
}

const iconOptions = transparencyOptions.map(({ value, label }) => ({
  value,
  label: (
    <>
      <TransparencyIcon transparency={value} className="mr-1" /> {label}
    </>
  ),
}));

function Edit(props: RenderEditCellProps<GridRow, any>): ReactNode {
  const controller = usePlanController();
  return (
    <DialogEditor
      {...props}
      submitHandler={async (values) =>
        await controller.formRequest({
          method: 'PUT',
          url: `plan/rows/${props.row.id}/client_total/`,
          body: values,
        })
      }
      dialogHeader="Client totals"
      testId="client-total-edit"
      initialValues={props.row.client_total}
    >
      <ButtonGroupField name="transparency" label="Invoice transparency" variant="primary" options={iconOptions} />

      <Box>
        <DecimalField name="budget" label="Budget" className="mb-1" precision={2} />
        <div className="form-group">
          <CheckField name="budget_include_smart_rates" fieldLabel="including smart rates" />
        </div>
      </Box>
    </DialogEditor>
  );
}

const titles = {
  DELIVERED: 'Delivered',
  PLANNED: 'Planned',
} as const;

function ViewBudget(props: RenderCellProps<GridGroup, GridSummaryRow>): ReactNode {
  const budget = props.row.budget ? parseBig(props.row.budget) : null;
  return (
    <Stack direction="horizontal" align="center">
      {budget && (
        <Box pr="2">
          <Budget budget={budget} amount={props.row.clientTotal} />
        </Box>
      )}
      <Box width="full" textAlign="right" title={fmt(props.row.clientTotal)} data-testid="amount">
        {fmt(props.row.clientTotal)}
      </Box>
    </Stack>
  );
}

function ViewSummaryBudget({ row: { clientTotal } }: RenderSummaryCellProps<GridSummaryRow, RowOrGroup>): ReactNode {
  return <Box textAlign="right">{fmt(clientTotal)}</Box>;
}

export const ClientTotalColumn = createColumn(
  { key: 'client_total', name: 'Client total', width: 156, className: clientColumn },
  {
    renderView: (props) => <View {...props} />,
    renderEdit: (props) => <Edit {...props} />,
    displayCellContent: true,
  },
  {
    renderView: (props) => <ViewBudget {...props} />,
    renderEdit: (props) => textEditor({ ...props, field: 'budget', className: budgetEditor }),
  },
  { renderSummaryCell: (props) => <ViewSummaryBudget {...props} /> },
);
